import ProductSectionSearch from "@/app/components/ui/Reusable/ProductSectionSearch";
import SearchableMultiSelectMenu from "@/app/components/ui/SelectMenus/MultiSelectMenu";
import SearchableSelectMenu from "@/app/components/ui/SelectMenus/SearchableSelectMenu";
import { useCouponCategoryAPI } from "@/app/hooks/api_hooks/coupon/useCouponCategoryAPI";
import { useStatusListAPI } from "@/app/hooks/api_hooks/Inventory/BadInventory/useStatusListAPI";
import { useClinicWarehouseDetailsByCategoryIdAPI } from "@/app/hooks/api_hooks/Inventory/ClinicToClinicTransfer/useClinicWarehouseDetailsByCategoryIdAPI";
import { useOrgCitiesListAPI } from "@/app/hooks/api_hooks/org/useOrgCitiesListAPI";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useSelectMenuReducer } from "@/app/hooks/useSelectMenuReducer";
import { useAppSelector } from "@/app_redux/hooks/root_hook";
import { IInventoryState } from "@/app_redux/reducers/slices/auth/inventory_slice";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";

export interface IInventoryBadRequestsFilterProps {
  cityId: string;
  clinics: Array<string>;
  productId: string;
  productName: string;
  category: Array<string>;
  reason: Array<string>;
  status: Array<string>;
}

const BadInventoryRequestsFilter = ({ setBadProductFilters }) => {
  const [selectedClinic, setSelectedClinic] = useState<string[]>([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string[]>([]);
  const { reasonList, statusList, clinicWarehouseDetails, cities } = useRouteInfo("INVENTORY")?.routeState
    ?.state as IInventoryState;
  const reasonOptions = useSelectMenuReducer(reasonList, "reason", "id");
  const statusOptions = useSelectMenuReducer(statusList, "status", "id");
  const clinicWarehouseOptions = useSelectMenuReducer(clinicWarehouseDetails, "clinic_name", "clinic_id");
  const cityOrgItem = useSelectMenuReducer(cities, "name", "id");
  const fetchCities = useOrgCitiesListAPI("INVENTORY");
  const fetchClinicWarehouseDetails = useClinicWarehouseDetailsByCategoryIdAPI();
  const fetchStatusList = useStatusListAPI();
  const { execute: fetchCategory, serviceCategory } = useCouponCategoryAPI();
  const couponCategoryItem = useSelectMenuReducer(serviceCategory, "category_name", "id");
  const { mappedCityId, mappedClinicId } = useAppSelector((state) => state.root.UIStates);
  const [initialFetch, setInitialFetch] = useState(false);
  const [initialRender, setInitialRender] = useState(false);

  const formHook = useForm<IInventoryBadRequestsFilterProps>({
    mode: "onChange",
    defaultValues: {
      cityId: mappedCityId,
      clinics: [mappedClinicId],
    },
  });

  useEffect(() => {
    fetchCities();
    fetchStatusList();
    fetchCategory();
  }, []);

  useEffect(() => {
    if (clinicWarehouseDetails?.length > 0 && !initialFetch && mappedClinicId) {
      formHook.setValue("clinics", [mappedClinicId]);
      setInitialFetch(true);
    }
  }, [clinicWarehouseDetails?.length > 0, mappedClinicId]);

  useEffect(() => {
    const data = formHook.getValues();
    localStorage.setItem("clinic_bad", mappedClinicId);
    if (
      data?.cityId &&
      data?.category?.length > 0 &&
      data?.clinics?.length > 0 &&
      data.reason?.length > 0 &&
      data.status?.length > 0 &&
      !initialRender
    ) {
      const selectedReasons = formHook.getValues("reason")?.map((reasonId) => {
        const foundOption = reasonOptions.find((option) => option.id === reasonId);
        return foundOption ? foundOption.title : "";
      });
      const selectedStatuses = formHook.getValues("status")?.map((statusId) => {
        const foundOption = statusOptions.find((option) => option.id === statusId);
        return foundOption ? foundOption.title : "";
      });
      const productId = formHook.getValues("productId");
      const filters = {
        clinic_ids: data.clinics,
        category_ids: data.category,
        reasons: data.reason,
        statuses: data.status,
      } as {
        clinic_ids: string[];
        category_ids: string[];
        reasons: string[];
        statuses: string[];
        vetic_variant_id?: string;
      };

      if (productId) {
        filters.vetic_variant_id = productId;
      }
      setBadProductFilters(filters);
      setInitialRender(true);
    }
  }, [
    formHook.watch("cityId"),
    formHook.watch("category"),
    formHook.watch("clinics"),
    formHook.watch("reason"),
    formHook.watch("status"),
  ]);

  useEffect(() => {
    const clinic_id = localStorage.getItem("clinic_bad");
    setTimeout(() => {
      formHook.setValue("clinics", [clinic_id]);
    }, 800);
  }, []);

  const Submit = () => {
    // if (selectedClinic?.length == 0) {
    //   toast.error("Clinic is Required");
    //   return;
    // }
    const selectedReasons = formHook.getValues("reason")?.map((reasonId) => {
      const foundOption = reasonOptions.find((option) => option.id === reasonId);
      return foundOption ? foundOption.title : "";
    });
    const selectedStatuses = formHook.getValues("status")?.map((statusId) => {
      const foundOption = statusOptions.find((option) => option.id === statusId);
      return foundOption ? foundOption.title : "";
    });
    const productId = formHook.getValues("productId");
    const filters = {
      clinic_ids: selectedClinic,
      category_ids: selectedCategoryId,
      reasons: selectedReasons,
      statuses: selectedStatuses,
    } as {
      clinic_ids: string[];
      category_ids: string[];
      reasons: string[];
      statuses: string[];
      vetic_variant_id?: string;
    };

    if (productId) {
      filters.vetic_variant_id = productId;
    }
    setBadProductFilters(filters);
  };

  return (
    <div>
      <h2 className="text-base font-semibold">Bad Inventory Requests</h2>
      <form className="w-full flex items-center gap-3" onSubmit={formHook.handleSubmit(Submit)}>
        <div className="w-full flex items-center gap-5">
          <div className="w-full">
            <SearchableSelectMenu
              errorMessages={[{ type: "required", message: "City is required" }]}
              onSelectItem={(item) => {
                if (item) {
                  formHook.setValue("clinics", []);
                  formHook.setValue("cityId", item.id);
                  const categoriesId = formHook.watch("category");
                  if (categoriesId?.length > 0 && item.id)
                    fetchClinicWarehouseDetails(categoriesId?.join(","), item.id);
                }
              }}
              fieldError={formHook.formState.errors.cityId}
              register={formHook.register("cityId", { required: true })}
              selectItems={cityOrgItem}
              placeholder="Select City"
              showTooltips={true}
              showTypedErrors={true}
              showDropdownIcon={true}
              defaultSelected={cityOrgItem?.filter((oc) => oc.id === formHook.watch("cityId"))[0]}
              listBoxClassName="w-full"
              className="text-gray-800"
              containerClassName="w-full"
            />
          </div>
          <div className="w-full">
            <SearchableMultiSelectMenu
              withSelectAll
              defaultSelectAll
              errorMessages={[{ type: "required", message: "Category is required" }]}
              onSelectItem={(item) => {
                if (item) {
                  formHook.setValue("clinics", []);
                  const categoriesId = item.map((i) => i.id);
                  const cityId = formHook.getValues("cityId");
                  formHook.setValue("category", categoriesId);
                  if (categoriesId?.length > 0 && cityId) fetchClinicWarehouseDetails(categoriesId?.join(","), cityId);
                  setSelectedCategoryId(categoriesId);
                }
              }}
              fieldError={formHook.formState.errors.category}
              register={formHook.register("category", { required: true })}
              selectItems={couponCategoryItem}
              placeholder="Select Category"
              showTooltips={false}
              showTypedErrors={false}
              showDropdownIcon={true}
              defaultSelected={couponCategoryItem?.filter((c) => formHook.watch("category")?.includes(c.id))}
              listBoxClassName="w-full"
              className="text-gray-800"
              containerClassName="w-full"
            />
          </div>
          <div className="w-full">
            <SearchableMultiSelectMenu
              withSelectAll
              errorMessages={[]}
              onSelectItem={(item) => {
                if (item) {
                  const clinicsId = item.map((i) => i.id);
                  formHook.setValue("clinics", clinicsId);
                  setSelectedClinic(clinicsId);
                }
              }}
              disabled={!formHook.getValues("cityId") || formHook.getValues("category")?.length == 0}
              fieldError={formHook.formState.errors.clinics}
              register={formHook.register("clinics")}
              selectItems={clinicWarehouseOptions}
              placeholder="Select Clinics"
              showTooltips={false}
              showTypedErrors={false}
              showDropdownIcon={true}
              defaultSelected={clinicWarehouseOptions?.filter((oc) => formHook.watch("clinics")?.includes(oc.id))}
              listBoxClassName="w-full"
              className="text-gray-800"
              containerClassName="w-full"
            />
          </div>
          <div className="flex items-center gap-2 pb-3 w-full">
            <ProductSectionSearch
              onSelectItem={(item) => {
                if (item) {
                  formHook.setValue("productId", item.id);
                  formHook.setValue("productName", item.name);
                } else {
                  formHook.setValue("productId", null);
                  formHook.setValue("productName", null);
                }
              }}
              containerClassName="mt-2 w-full shadow-none"
              itemActiveClassName="bg-orange-500 text-white"
              inputContainerClassName="w-full"
              flexibleAdd={false}
              searchType="Lab"
            />
          </div>
          <div className="w-full">
            <SearchableMultiSelectMenu
              withSelectAll
              defaultSelectAll
              errorMessages={[]}
              onSelectItem={(item) => {
                if (item) {
                  const reasonsId = item.map((i) => i.id);
                  formHook.setValue("reason", reasonsId);
                }
              }}
              fieldError={formHook.formState.errors.reason}
              register={formHook.register("reason")}
              selectItems={reasonOptions}
              placeholder="Select Reasons"
              showTooltips={false}
              showTypedErrors={false}
              showDropdownIcon={true}
              defaultSelected={reasonOptions?.filter((c) => formHook.watch("reason")?.includes(c.id))}
              listBoxClassName="w-full"
              className="text-gray-800 "
              containerClassName="w-full"
            />
          </div>
          <div className="w-full">
            <SearchableMultiSelectMenu
              withSelectAll
              defaultSelectAll
              errorMessages={[]}
              onSelectItem={(item) => {
                if (item) {
                  const statusId = item.map((i) => i.id);
                  formHook.setValue("status", statusId);
                }
              }}
              fieldError={formHook.formState.errors.status}
              register={formHook.register("status")}
              selectItems={statusOptions}
              placeholder="Select Status"
              showTooltips={false}
              showTypedErrors={false}
              showDropdownIcon={true}
              defaultSelected={statusOptions?.filter((c) => formHook.watch("status")?.includes(c.id))}
              listBoxClassName="w-full"
              className="text-gray-800"
              containerClassName="w-full"
            />
          </div>
        </div>
        <div>
          <input
            type="submit"
            className="py-2 px-6 border-transparent bg-black rounded-md text-white text-sm cursor-pointer"
            value={"Search"}
          />
        </div>
      </form>
    </div>
  );
};

export default BadInventoryRequestsFilter;
